<script setup lang="ts">
    import { computed, shallowReactive, onMounted, watch, ref } from 'vue'
    import { useRoute, useRouter } from 'vue-router'
    import { toPercentageString, toStringSmartly } from '@common/Common'
    import { guid } from '@common/Guid'
    import PortfolioNavigation from '@common/Components/PortfolioNavigation.vue'
    import Navigation from '@common/Navigation.vue'
    import CapexItemEditor from '@Quest/Capex/Components/CapexItemEditor.vue'
    import ProjectNavigation from '@Quest/ProjectPlans/Components/ProjectNavigation.vue'
    import ProjectContextHeader from '@Quest/ProjectPlans/Components/ProjectContextHeader.vue'
    import { DepreciationMethodType } from '@Quest/Capex/types'
    import { useProjectCapexQueryV1 } from '@Quest/ProjectPlans/queries-vue-composables'
    import { useCreateProjectCapexItemCommandV1, useUpdateProjectCapexItemCommandV1, useDeleteProjectCapexItemCommandV1 } from '@Quest/ProjectPlans/commands-vue-composables'
    import type { ProjectCapexItemReadModel, ProjectPlanCapexReadModel } from '@Quest/ProjectPlans/readmodels'
    import { CapexItemEditorFormSubmitEventPayload, CapexItemEditorViewModel } from '@Quest/Capex/Components/CapexItemEditor'
    import type { CurrencyDenomination } from '@common/types'

    const route = useRoute()
    const router = useRouter()
    const workspaceId = computed(() => Number.parseInt(route.params.workspaceId as string))
    const tchProjectId = computed(() => Number(route.params.tchProjectId ?? 0))
    const projectId = computed(() => String(route.params.projectId ?? ''))

    const projectPlanId = computed<string | null>(() => rm.value?.ProjectPlanId ?? null)
    const denomination = computed<CurrencyDenomination | null>(() => rm.value?.Denomination ?? null)

    const projectName = computed(() => rm.value?.ProjectName ?? '')
    const projectPlanName = computed<string>(() => rm.value?.ProjectPlanName ?? '')
    const activePlanId = computed<string | null>(() => rm.value?.ActiveProjectPlanId ?? null)
        
    const portfolioId = computed(() => rm.value?.PortfolioId ?? '')
    const userId = computed(() => rm.value?.UserId ?? 0)
    const isWorkspaceMember = computed(() => rm.value?.IsWorkspaceMember ?? false)
    const isProjectOwner = computed(() => rm.value?.IsProjectOwner ?? false)
    const isSubmitted = computed(() => rm.value?.IsSubmitted ?? false)
    

    const {
        runProjectCapexQueryV1,
        modelProjectCapexQueryV1
    } = useProjectCapexQueryV1(workspaceId, tchProjectId, projectId, projectPlanId, denomination)

    const rm = ref<ProjectPlanCapexReadModel | null>(null)

    const capexItemEditor = shallowReactive(new CapexItemEditorViewModel())

    const {
        invokeCreateProjectCapexItemCommandV1,
        onSucceedCreateProjectCapexItemCommandV1
    } = useCreateProjectCapexItemCommandV1(userId, workspaceId, portfolioId, projectId, projectPlanId)

    const {
        invokeUpdateProjectCapexItemCommandV1,
        onSucceedUpdateProjectCapexItemCommandV1
    } = useUpdateProjectCapexItemCommandV1(userId, workspaceId, portfolioId, projectId, projectPlanId)

    const {
        invokeDeleteProjectCapexItemCommandV1,
        onSucceedDeleteProjectCapexItemCommandV1,
    } = useDeleteProjectCapexItemCommandV1(userId, workspaceId, portfolioId, projectId, projectPlanId)

    function fmtNum(val: number | null)
    {
        return toStringSmartly(val)
    }

    function fmtPct(val: number | null)
    {
        return toPercentageString(val)
    }


    function showCapexCreator()
    {
        if (!rm.value) return
        capexItemEditor.showCreator(rm.value.CurrencyLabel, rm.value.CapexAccounts)
    }

    function showCapexEditor(c: ProjectCapexItemReadModel)
    {
        if (!rm.value) return

        capexItemEditor.showEditor(
            c.Id,
            c.Name,
            c.CapexAccountId,
            c.Cost,
            c.UsefulLifeInYears,
            c.AcquisitionMonthIndex,
            c.SalvageValue,
            rm.value.CurrencyLabel,
            rm.value.CapexAccounts
        )
    }

    function handleCapexItemEditorFormSubmit(e: CapexItemEditorFormSubmitEventPayload)
    {
        if (!rm.value) return

        const payload = {
            itemId: e.id ?? guid(),
            capexAccountId: e.accountId,
            name: e.name,
            cost: e.cost,
            acquisitionMonthIndex: e.acquisitionMonthIndex,
            salvageValue: e.salvageValue,
            usefulLifeInYears: e.usefulLifeInYears,
            depreciationMethod: DepreciationMethodType.StraightLine,
            denomination: rm.value.Denomination
        }

        if (e.id == null)
        {
            invokeCreateProjectCapexItemCommandV1(payload)
        }
        else
        {
            invokeUpdateProjectCapexItemCommandV1(payload)
        }
    }

    function handleDeleteProjectCapexItemCommand(capex: ProjectCapexItemReadModel)
    {
        if (!rm.value) return

        if (confirm('Are you sure?'))
        {
            invokeDeleteProjectCapexItemCommandV1({ itemId: capex.Id })
        }
    }

    function setDenomination(newDenomination: CurrencyDenomination)
    {
        const query = { ...route.query }

        query.denomination = newDenomination

        router.replace({
            params: { ...route.params },
            query
        })

        runProjectCapexQueryV1(
        {
            Denomination: newDenomination,
        })
    }


    onMounted(() =>
    {
        document.title = 'Capex | TransparentChoice'

        runProjectCapexQueryV1({
            ProjectPlanId: route.params.planId ? String(route.params.planId) : null,
            Denomination: route.query.denomination as CurrencyDenomination ?? null
        })
    })

    watch(() => route.params.planId, () => 
    {
        runProjectCapexQueryV1({
            ProjectPlanId: route.params.planId as string | null
        })
    })

    watch(modelProjectCapexQueryV1, (current) =>
    {
        if (current?.RedirectUrl)
        {
            window.location.replace(current.RedirectUrl)
        }

        if (current)
        {
            rm.value = current
        }
    })


    onSucceedCreateProjectCapexItemCommandV1(() =>
    {
        capexItemEditor.close()
        runProjectCapexQueryV1()
    })

    onSucceedUpdateProjectCapexItemCommandV1(() =>
    {
        capexItemEditor.close()
        runProjectCapexQueryV1()
    })

    onSucceedDeleteProjectCapexItemCommandV1(() =>
    {
        runProjectCapexQueryV1()
    })

</script>

<template>
    <div>
        <CapexItemEditor :viewModel="capexItemEditor"
                         @form-submit="handleCapexItemEditorFormSubmit" />

        <PortfolioNavigation v-if="isWorkspaceMember" :workspaceId="workspaceId" :projectId="tchProjectId" />
        <Navigation v-else-if="isProjectOwner" />

        <div v-if="rm" class="container-fluid" style="margin-top: 80px; padding-top: 40px;">
            <div class="row">
                <div class="col-lg-6">
                    <ProjectContextHeader :projectName="projectName"
                                          :planId="projectPlanId"
                                          :planName="projectPlanName"
                                          :activePlanId="activePlanId" />
                </div>
                <div class="col-lg-6 text-right align-self-end">

                    <!-- When NOT submitted -->
                    <button class="btn btn-success"
                            style="padding: 0.7em 2em;"
                            v-if="!isSubmitted"
                            @click="showCapexCreator">
                        Create
                    </button>

                    <!-- When submitted -->
                    <button class="btn btn-outline-secondary"
                            style="padding: 0.7em 2em; cursor: default;"
                            v-if="isSubmitted"
                            disabled
                            title="You cannot edit a submitted plan">
                        <i class="fas fa-lock mr-2"></i> Submitted
                    </button>
                </div>
            </div>
            <hr />

            <ProjectNavigation ActivePage="capex" class="mt-4 mb-4"
                               :workspaceId="workspaceId"
                               :tchProjectId="tchProjectId"
                               :projectId="projectId"
                               :planId="projectPlanId" />

            <div class="toolbar">
                <div>
                    <div class="dropdown d-inline mr-2">
                        <button class="btn btn-outline-secondary dropdown-toggle" type="button" data-toggle="dropdown">
                            {{denomination}}s
                        </button>
                        <div class="dropdown-menu">
                            <a class="dropdown-item" @click="setDenomination('Million')">Millions</a>
                            <a class="dropdown-item" @click="setDenomination('Thousand')">Thousands</a>
                            <a class="dropdown-item" @click="setDenomination('Unit')">Units</a>
                        </div>
                    </div>
                </div>
                <div></div>
            </div>


            <table class="table table-bordered table-hover">
                <thead>
                    <tr>
                        <th>Capex</th>
                        <th>Account</th>
                        <th>Cost ({{rm.CurrencyLabel}})</th>
                        <th>Salvage value ({{rm.CurrencyLabel}})</th>
                        <th>Useful life (years)</th>
                        <th>Annual expense ({{rm.CurrencyLabel}})</th>
                        <th style="width: 150px;"></th>
                    </tr>
                </thead>
                <tbody>

                    <tr v-for="c in rm.CapexItems">
                        <td>{{c.Name}}</td>
                        <td>{{c.CapexAccountName}}</td>
                        <td>{{fmtNum(c.Cost)}}</td>
                        <td>{{fmtNum(c.SalvageValue)}}</td>
                        <td>{{c.UsefulLifeInYears}}</td>
                        <td>{{fmtNum(c.AnnualExpense)}}</td>
                        <td>
                            <!-- If NOT submitted: show actions dropdown -->
                            <div class="dropdown" v-if="!isSubmitted">
                                <button type="button" class="btn btn-outline-secondary dropdown-toggle" data-toggle="dropdown">
                                    Actions
                                </button>
                                <div class="dropdown-menu dropdown-menu-right">
                                    <a class="dropdown-item" @click="showCapexEditor(c)">Edit</a>
                                    <a class="dropdown-item" @click="handleDeleteProjectCapexItemCommand(c)">Delete</a>
                                </div>
                            </div>

                            <!-- If submitted: show disabled button with lock icon -->
                            <button v-if="isSubmitted" class="btn btn-outline-secondary"
                                    disabled
                                    style="cursor: default;"
                                    title="You cannot modify a submitted plan">
                                <i class="fas fa-lock mr-2"></i> Submitted
                            </button>
                        </td>
                    </tr>

                </tbody>
            </table>

        </div>
    </div>
</template>

<style scoped>
    .toolbar {
        background-color: #f8f8f8; /* Light gray background for the toolbar */
        padding: 10px 15px; /* Padding around the toolbar */
        border: 1px solid #e4e4e4; /* A light border to give separation */
        display: flex; /* Use flex to give layout flexibility */
        align-items: center; /* Center-align items vertically */
        justify-content: space-between; /* Separate items */
    }
</style>
